import posthog from 'posthog-js'
import { create } from 'zustand'
import type { User } from './api/client'

export const fixtureFlags = {
  dashboard: 'dashboard-use-fixtures',
  inventory: 'inventory-use-fixtures',
  pos: 'pos-use-fixtures',
  shell: 'shell-use-fixtures',
} as const
export type Feature = keyof typeof fixtureFlags
export const inventoryActionFlags = {
  edit: 'inventory-edit-enabled',
  adjust: 'inventory-adjust-enabled',
  delete: 'inventory-delete-enabled',
} as const
export type InventoryAction = keyof typeof inventoryActionFlags
export type InventoryActions = Record<InventoryAction, boolean>
const disabledInventoryActions: InventoryActions = {
  edit: false,
  adjust: false,
  delete: false,
}
export type DataSource = 'fixture' | 'api'
type FlagState = {
  scope: string
  status: 'loading' | 'ready' | 'error'
  sources: Record<Feature, DataSource>
  inventoryActions: InventoryActions
  revision: number
}

const apiSources: Record<Feature, DataSource> = {
  dashboard: 'api',
  inventory: 'api',
  pos: 'api',
  shell: 'api',
}
export const useDataSourceStore = create<FlagState>(() => ({
  scope: '',
  status: 'loading',
  sources: apiSources,
  inventoryActions: disabledInventoryActions,
  revision: 0,
}))
let unsubscribe: (() => void) | undefined
let timer: ReturnType<typeof setTimeout> | undefined
let currentUser: User | undefined

export function userScope(user: User) {
  return `${user.id}:${user.storeId}`
}

export function sourcesFromFlags(
  values: Record<string, boolean | string>,
): Record<Feature, DataSource> {
  const result = { ...apiSources }
  for (const feature of Object.keys(fixtureFlags) as Feature[]) {
    const value = values[fixtureFlags[feature]]
    if (value !== undefined && typeof value !== 'boolean')
      throw new Error('Expected boolean fixture flag')
    result[feature] = value === true ? 'fixture' : 'api'
  }
  return result
}

export function inventoryActionsFromFlags(
  values: Record<string, unknown>,
): InventoryActions {
  return {
    edit: values[inventoryActionFlags.edit] === true,
    adjust: values[inventoryActionFlags.adjust] === true,
    delete: values[inventoryActionFlags.delete] === true,
  }
}

export function isInventoryActionEnabled(
  scope: string,
  action: InventoryAction,
) {
  const state = useDataSourceStore.getState()
  return (
    !!scope &&
    state.scope === scope &&
    state.status === 'ready' &&
    state.inventoryActions[action]
  )
}

export function useInventoryActions(user?: User): InventoryActions {
  const state = useDataSourceStore()
  return user && state.scope === userScope(user) && state.status === 'ready'
    ? state.inventoryActions
    : disabledInventoryActions
}

export function resetDataSources() {
  unsubscribe?.()
  unsubscribe = undefined
  clearTimeout(timer)
  currentUser = undefined
  useDataSourceStore.setState({
    scope: '',
    status: 'loading',
    sources: apiSources,
    inventoryActions: disabledInventoryActions,
    revision: 0,
  })
}

export function loadDataSources(user: User) {
  unsubscribe?.()
  clearTimeout(timer)
  currentUser = user
  const scope = userScope(user)
  useDataSourceStore.setState({
    scope,
    status: 'loading',
    inventoryActions: disabledInventoryActions,
  })
  if (
    !import.meta.env.VITE_POSTHOG_KEY ||
    import.meta.env.VITE_POSTHOG_KEY.includes('replace_')
  ) {
    useDataSourceStore.setState({ status: 'error' })
    return
  }
  const fail = () => {
    if (currentUser === user)
      useDataSourceStore.setState({
        status: 'error',
        inventoryActions: disabledInventoryActions,
      })
  }
  // SDK callbacks with no context are synchronous cached snapshots. Only a
  // completed evaluation for the identified user may select a business source.
  const stopFlags = posthog.onFeatureFlags((_enabled, values, context) => {
    if (
      !context ||
      currentUser !== user ||
      posthog.get_distinct_id() !== user.id
    )
      return
    clearTimeout(timer)
    if (context.errorsLoading) {
      fail()
      return
    }
    try {
      const sources = sourcesFromFlags(values)
      const previous = useDataSourceStore.getState()
      const changed = Object.keys(fixtureFlags).some(
        (key) => previous.sources[key as Feature] !== sources[key as Feature],
      )
      useDataSourceStore.setState({
        status: 'ready',
        sources,
        inventoryActions: inventoryActionsFromFlags(values),
        revision: previous.revision + Number(changed),
      })
    } catch {
      fail()
    }
  })
  const refresh = () => {
    clearTimeout(timer)
    timer = setTimeout(fail, 10_000)
    posthog.reloadFeatureFlags()
  }
  window.addEventListener('focus', refresh)
  unsubscribe = () => {
    stopFlags()
    window.removeEventListener('focus', refresh)
  }
  timer = setTimeout(fail, 10_000)
  posthog.reloadFeatureFlags()
}

export function retryDataSources() {
  if (currentUser) loadDataSources(currentUser)
}

export function useDataSource(feature: Feature, user?: User) {
  const state = useDataSourceStore()
  const scope = user ? userScope(user) : ''
  const status = scope && state.scope === scope ? state.status : 'loading'
  return {
    source: state.sources[feature],
    status,
    scope,
    revision: state.revision,
    retry: retryDataSources,
  }
}

export function assertLivePos(scope: string) {
  const state = useDataSourceStore.getState()
  if (
    state.scope !== scope ||
    state.status !== 'ready' ||
    state.sources.pos !== 'api'
  ) {
    throw new Error('DATA_SOURCE_CHANGED')
  }
}
